import { Input } from 'antd';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { FormField } from '../form-field';

import styles from './otp-field.module.scss';

export type OtpFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  help?: string;
  required?: boolean;
  /** Number of cells. Even lengths render a dash between the two halves. */
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
};

export function OtpField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  label,
  help,
  required,
  length = 6,
  disabled,
  autoFocus,
}: OtpFieldProps<TFieldValues, TName>) {
  const dashIndex = length % 2 === 0 ? length / 2 - 1 : -1;
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <div className={styles.otp}>
          <Input.OTP
            autoFocus={autoFocus}
            length={length}
            disabled={disabled}
            status={fieldState.error ? 'error' : undefined}
            separator={(index: number) =>
              index === dashIndex ? <span className={styles.dash} aria-hidden /> : null
            }
            value={field.value ?? ''}
            // onInput fires on every change (incl. deletions); onChange only fires
            // when all cells are full, which would leave a stale value in RHF after
            // a delete. Drive RHF off onInput so the value always tracks the cells.
            onInput={(cells) => field.onChange(cells.join(''))}
          />
        </div>
      )}
    </FormField>
  );
}
